import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import React from 'react';
import { useTranslation } from 'react-i18next';
import { BiData, PureBiData } from '@wix/da-bi/pkg/events';
import { BiEventType, ReferralType } from '@wix/da-bi/pkg/constants';
import StandardMenu from '../../../../Dropdown/MenuPreset/StandardMenu';
import { getDeviationResourceType } from '../../../../DeviationViews/_helpers';
import { useVerification } from '../../../../VerificationPopup/withVerification';
import { ElementVisibility, useElementVisibilityForArea } from '../../../ElementVisibilityContext';
import { useFreeDownloadAction, usePremiumDownloadAction } from '../../DownloadButton/hooks';
import s from './MoreMenuContent.scss';
/**
 * Takes an array of actions that show popups and returns them with their dropdown entry's `onClick` callback
 * altered to call the supplied {@link callback} when executed.
 */ function withExtraCallbackInActionOnClick(actionsWithPopups, callback) {
    return Object.entries(actionsWithPopups).reduce((accumulator, [key, value])=>_object_spread_props(_object_spread({}, accumulator), {
            [key]: value ? _object_spread_props(_object_spread({}, value), {
                action: _object_spread_props(_object_spread({}, value.action), {
                    onClick: (e)=>{
                        value.action.onClick && value.action.onClick(e);
                        callback();
                    }
                })
            }) : value
        }), {});
}
// This is used only on desktop
const MoreMenuContent = ({ deviation, isOwner, closeTooltip = ()=>null, showReportModal, showNftLinkPopup, shareDeviationToPost, showAwardBadgePopup, onToggleCommentPanel, actionsWithPopups })=>{
    var _deviation_author, _actionsWithPopupsAndAugmentedOnClick_addToGroup, _actionsWithPopupsAndAugmentedOnClick_privateCollection, _actionsWithPopupsAndAugmentedOnClick_share;
    const { t } = useTranslation();
    const { restrictToVerifiedAndLoggedIn } = useVerification();
    const elementVisibilityMap = useElementVisibilityForArea(ElementVisibility.Menu);
    const freeDownloadAction = useFreeDownloadAction(deviation, isOwner);
    const premiumDownloadAction = usePremiumDownloadAction(deviation, isOwner);
    const actionsWithPopupsAndAugmentedOnClick = withExtraCallbackInActionOnClick(actionsWithPopups, closeTooltip);
    var _deviation_author_useridUuid;
    return /*#__PURE__*/ React.createElement(StandardMenu, {
        className: s['menu'],
        id: "actionbar-more-menu",
        items: [
            elementVisibilityMap.comment && {
                value: 'comment',
                label: t('actionbar.comment.label'),
                onClick: onToggleCommentPanel,
                biData: BiData({
                    evid: 105,
                    typeid: getDeviationResourceType(deviation),
                    itemid: deviation.deviationId,
                    creatorid: (_deviation_author_useridUuid = (_deviation_author = deviation.author) === null || _deviation_author === void 0 ? void 0 : _deviation_author.useridUuid) !== null && _deviation_author_useridUuid !== void 0 ? _deviation_author_useridUuid : ''
                })
            },
            elementVisibilityMap.premiumDownload && premiumDownloadAction.hasAction && {
                value: 'download',
                label: t('actionbar.premiumDownload'),
                link: premiumDownloadAction.href,
                onClick: ()=>{
                    premiumDownloadAction.onClick && premiumDownloadAction.onClick();
                    closeTooltip();
                },
                biData: PureBiData({
                    evid: 350,
                    ispaiddownload: 1,
                    deviationid: deviation.deviationId,
                    sectionname: 'action_bar'
                })
            },
            elementVisibilityMap.freeDownload && freeDownloadAction.hasAction && {
                value: 'download',
                label: t('actionbar.freeDownload'),
                link: freeDownloadAction.href,
                onClick: ()=>{
                    freeDownloadAction.onClick && freeDownloadAction.onClick();
                    closeTooltip();
                },
                biData: PureBiData({
                    evid: 350,
                    ispaiddownload: 0,
                    deviationid: deviation.deviationId,
                    sectionname: 'action_bar'
                })
            },
            (_actionsWithPopupsAndAugmentedOnClick_addToGroup = actionsWithPopupsAndAugmentedOnClick.addToGroup) === null || _actionsWithPopupsAndAugmentedOnClick_addToGroup === void 0 ? void 0 : _actionsWithPopupsAndAugmentedOnClick_addToGroup.action,
            elementVisibilityMap.awardBadge && {
                value: 'awardBadge',
                label: t('actionbar.awardBadge'),
                onClick: ()=>{
                    showAwardBadgePopup(deviation);
                    closeTooltip();
                },
                biData: {
                    evid: BiEventType.GIVE_BADGE_AWARD,
                    sectionname: 'deviation',
                    typeid: deviation.typeId,
                    itemid: deviation.deviationId
                }
            },
            (_actionsWithPopupsAndAugmentedOnClick_privateCollection = actionsWithPopupsAndAugmentedOnClick.privateCollection) === null || _actionsWithPopupsAndAugmentedOnClick_privateCollection === void 0 ? void 0 : _actionsWithPopupsAndAugmentedOnClick_privateCollection.action,
            (_actionsWithPopupsAndAugmentedOnClick_share = actionsWithPopupsAndAugmentedOnClick.share) === null || _actionsWithPopupsAndAugmentedOnClick_share === void 0 ? void 0 : _actionsWithPopupsAndAugmentedOnClick_share.action,
            elementVisibilityMap.share && {
                value: 'shareToPost',
                label: t('actionbar.shareToPost'),
                onClick: restrictToVerifiedAndLoggedIn(ReferralType.SHARE_TO_POST, 'submitstatus', ()=>shareDeviationToPost(deviation)),
                biData: BiData({
                    evid: 356,
                    typeid: getDeviationResourceType(deviation),
                    itemid: deviation.deviationId,
                    social_share: 'da_post',
                    deviationid: deviation.deviationId
                })
            },
            elementVisibilityMap.report && {
                value: 'report',
                label: t('actionbar.report'),
                biData: BiData({
                    evid: 20,
                    modal_version: 2,
                    deviationid: deviation.deviationId
                }),
                onClick: ()=>{
                    showReportModal(deviation);
                    closeTooltip();
                }
            },
            elementVisibilityMap.linkNft && {
                value: 'linknft',
                label: t('actionbar.linkNft'),
                onClick: ()=>{
                    showNftLinkPopup(deviation);
                    closeTooltip();
                }
            }
        ]
    });
};
MoreMenuContent.displayName = 'MoreMenuContent';
export default MoreMenuContent;
